import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useRevalidator } from "react-router-dom";

import { colors, palette, radius } from "../theme/tokens";

import { PdfScroll } from "./PdfScroll";

// An agreement that is out for signature (2026-10-14, Bill): everyone in the
// room reads the document as DocuSign holds it now, with every signature
// already applied (`.../agreements/{itemId}/document`), never the draft and
// with no status block above it. A signer who has not signed yet also gets
// "Review and sign here", which swaps the reader for DocuSign's focused view
// (`POST .../sign`, mounted with DocuSign JS). Whether the viewer still has to
// sign is asked of DocuSign (`.../live`), not read from the room, whose
// stored envelope state only moves on envelope events and so still says
// "awaiting" after one signer has signed.

interface DocuSignSigning {
    on(event: "sessionEnd", cb: (e: { sessionEndType?: string }) => void): void;
    mount(el: HTMLElement): void;
}
interface DocuSignJs {
    loadDocuSign(clientId: string): Promise<{ signing(opts: Record<string, unknown>): DocuSignSigning }>;
}
declare global {
    interface Window {
        DocuSign?: DocuSignJs;
    }
}

let bundle: Promise<void> | null = null;
function loadBundle(): Promise<void> {
    bundle ??= new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://js.docusign.com/bundle.js";
        s.onload = () => resolve();
        s.onerror = () => {
            bundle = null;
            reject(new Error("DocuSign could not be loaded"));
        };
        document.head.appendChild(s);
    });
    return bundle;
}

const MESSAGES: Record<string, string> = {
    RECIPIENT_NOT_IN_SEQUENCE: "This agreement opens for you once the earlier signer has signed.",
    not_embedded: "This envelope can only be signed from the DocuSign email.",
    not_configured: "Signing in the room is not switched on yet. Please sign from the DocuSign email.",
};
const FAILED = "DocuSign could not open the agreement. Please try again, or sign from the DocuSign email.";

export function SignInRoom({ slug, itemId, title }: { slug: string; itemId: string; title: string }) {
    const revalidator = useRevalidator();
    const host = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<"idle" | "loading" | "open" | { error: string }>("idle");
    // The viewer's own signer status from DocuSign: null = not a signer or
    // not known yet. Bumping `version` re-reads it and reloads the document.
    const [me, setMe] = useState<string | null>(null);
    const [version, setVersion] = useState(0);
    const base = `/api/rooms/${slug}/agreements/${encodeURIComponent(itemId)}`;

    useEffect(() => {
        let cancelled = false;
        fetch(`${base}/live`, { cache: "no-store" })
            .then((r) => (r.ok ? r.json() : null))
            .then((body: { me?: string | null } | null) => {
                if (!cancelled) setMe(body?.me ?? null);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [base, version]);

    async function open() {
        setState("loading");
        try {
            const res = await fetch(`${base}/sign`, { method: "POST" });
            const body = (await res.json()) as { url?: string; integrationKey?: string; error?: string };
            if (!res.ok || !body.url || !body.integrationKey) {
                if (body.error === "already_signed") {
                    setMe("completed");
                    setState("idle");
                    return;
                }
                setState({ error: MESSAGES[body.error ?? ""] ?? FAILED });
                return;
            }
            await loadBundle();
            const ds = await window.DocuSign!.loadDocuSign(body.integrationKey);
            const signing = ds.signing({
                url: body.url,
                displayFormat: "focused",
                style: {
                    branding: { primaryButton: { backgroundColor: palette.blue, color: "#FFFFFF" } },
                    signingNavigationButton: { finishText: "Finish signing", position: "bottom-center" },
                },
            });
            signing.on("sessionEnd", () => {
                // Signed or not, go back to the document as DocuSign now holds
                // it; the live status decides whether the button comes back.
                setState("idle");
                setVersion((v) => v + 1);
                revalidator.revalidate();
            });
            setState("open");
            // The host is rendered once state is "open"; mount on the next frame.
            requestAnimationFrame(() => host.current && signing.mount(host.current));
        } catch {
            setState({ error: FAILED });
        }
    }

    if (state === "open") {
        return (
            <Box
                ref={host}
                sx={{
                    flex: 1,
                    minHeight: 640,
                    border: 1,
                    borderColor: colors.line,
                    borderRadius: `${radius.lg}px`,
                    overflow: "hidden",
                    "& iframe": { width: "100%", height: "100%", minHeight: 640, border: 0 },
                }}
            />
        );
    }
    const toSign = me !== null && me !== "completed";
    return (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            {toSign && (
                <Box>
                    <Button variant="contained" color="secondary" onClick={open} disabled={state === "loading"}>
                        {state === "loading" ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Review and sign here"}
                    </Button>
                    {typeof state === "object" && (
                        <Typography variant="body2" sx={{ mt: 1.5, color: colors.darkGray }}>
                            {state.error}
                        </Typography>
                    )}
                </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 360 }}>
                <PdfScroll key={version} url={`${base}/document?v=${version}`} title={title} />
            </Box>
        </Box>
    );
}
